import { Field, Form, Formik, FormikProps } from "formik";
import { ScrollToFirstError } from "utils";
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  SelectComponent,
  AsyncSelectComponent,
  CalendarInput,
  DropFileInput,
  LargeSwitch,
} from "shared";
import {
  getFormConfig,
  getValidationSchema,
  getInitialValues,
  InvestmentFormValues,
  InvestmentFieldConfig,
} from "./forms";
import RadioGroup from "./fields/RadioGroup";
import ComputedField from "./fields/ComputedField";
import { useGetCountriesCurrency } from "hooks/countries";
import {
  useGetInvestmentCurrencies,
  searchInvestmentsFetcher,
} from "hooks/investments";
import "./InvestmentForm.scss";

interface InvestmentFormProps {
  slug: string;
  onSubmit: (values: InvestmentFormValues) => void;
  onCancel: () => void;
  initialData?: Partial<InvestmentFormValues>;
  isLoading?: boolean;
  submitLabel?: string;
  isAdmin?: boolean;
  checkIfCurrencyExists?: (currencyUuid: string) => boolean;
  checkIfCryptoExists?: (currencyUuid: string) => boolean;
}

const renderField = (
  field: InvestmentFieldConfig,
  formik: FormikProps<InvestmentFormValues>,
  currencyOptions?: any[],
  isCurrencyTransaction?: boolean,
  isAdmin?: boolean,
  isExistingCryptoTransaction?: boolean,
) => {
  const { name, label, type, placeholder } = field;

  // Ukryj pola "additionalInfo" i "relianceCanView" dla transakcji walutowych
  if (
    isCurrencyTransaction &&
    (name === "additionalInfo" || name === "relianceCanView")
  ) {
    return null;
  }

  if (
    isExistingCryptoTransaction &&
    (name === "status" ||
      name === "additionalInfo" ||
      name === "relianceCanView")
  ) {
    return null;
  }

  if (isAdmin && name === "relianceCanView") {
    return null;
  }

  if (field.adminOnly && !isAdmin) {
    return null;
  }

  if (field.clientOnly && isAdmin) {
    return null;
  }

  if (
    field.showWhen &&
    String(formik.values[field.showWhen.field] || "") !== field.showWhen.value
  ) {
    return null;
  }

  const error =
    (formik.touched[name] || formik.submitCount > 0) && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  const resolvedLabel =
    (isCurrencyTransaction || isExistingCryptoTransaction) &&
    name === "purchaseDate"
      ? "Data transakcji"
      : label;

  switch (type) {
    case "text":
    case "number": {
      const resolvedSuffix = field.computeSuffix
        ? field.computeSuffix(formik.values, currencyOptions)
        : field.suffix;
      const isDisabled = field.computeDisabled
        ? field.computeDisabled(formik.values)
        : false;
      return (
        <div className="investment-form__field" key={name}>
          <Field
            as={Input}
            name={name}
            label={resolvedLabel}
            type={type}
            placeholder={placeholder}
            step={field.step}
            precision={field.precision}
            inputMode={field.inputMode}
            maxLength={field.maxLength}
            optional={field.required === false && !isDisabled}
            error={error}
            data-suffix={resolvedSuffix}
            disabled={isDisabled}
          />
        </div>
      );
    }
    case "textarea":
      return (
        <div className="investment-form__field" key={name}>
          <Field
            component={Textarea}
            name={name}
            label={resolvedLabel}
            placeholder={placeholder}
            optional={field.required === false}
            error={error}
          />
        </div>
      );
    case "async-select": {
      const loadOptions = async (inputValue: string) => {
        const q = inputValue.trim();
        if (q.length < 2) return [];
        const items = await searchInvestmentsFetcher(q, field.asyncSlug);
        return items.map((inv) => ({
          label: inv.name ?? "",
          value: inv.name ?? "",
          investment: inv,
        }));
      };
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            id={String(name)}
            name={String(name)}
            className="select--no-arrow"
            label={resolvedLabel}
            as={AsyncSelectComponent as any}
            loadOptions={loadOptions}
            minCharsToSearch={2}
            optional={field.required === false}
            onSelectOption={(opt: any) => {
              const inv = opt?.investment;
              if (inv) {
                if (field.onSelectSideEffect) {
                  field.onSelectSideEffect(inv, formik.setFieldValue);
                }
                // Zawsze zapisz uuid wybranej inwestycji jako parentInvestmentUuid
                formik.setFieldValue("parentInvestmentUuid", inv.uuid);
              }
            }}
            onInputTyping={() => {
              formik.setFieldValue("parentInvestmentUuid", "");
              // Wyczyść pre-wypełnione pola gdy użytkownik zmienia wpisaną nazwę
              if (field.onSelectSideEffect) {
                field.onSelectSideEffect(null, formik.setFieldValue);
              }
            }}
          />
        </div>
      );
    }
    case "select": {
      const rawOptions =
        name === "currencyUuid" ? currencyOptions ?? [] : field.options ?? [];
      const selectOptions = field.excludeLabels?.length
        ? rawOptions.filter((o: any) => !field.excludeLabels!.includes(o.label))
        : rawOptions;
      const isSelectDisabled = field.computeDisabled
        ? field.computeDisabled(formik.values)
        : false;
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            as={SelectComponent}
            options={selectOptions}
            name={name}
            label={resolvedLabel}
            isSearchable={field.isSearchable ?? false}
            optional={field.required === false}
            disabled={isSelectDisabled}
          />
          {isCurrencyTransaction && name === "currencyUuid" && (
            <div className="input__hint" aria-live="polite">
              Miałeś już zakupioną tę walutę. Kontynuując, dodasz nową
              transakcję do swojej puli.
            </div>
          )}
        </div>
      );
    }
    case "checkbox":
      return (
        <div
          className={`investment-form__field${
            field.wrapperClassName ? ` ${field.wrapperClassName}` : ""
          }`}
          key={String(name)}
        >
          <Field
            as={Checkbox}
            name={String(name)}
            label={field.consentText || resolvedLabel}
            checked={formik.values[name]}
            onInputChange={() => {
              const newValue = !formik.values[name];
              if (field.onChangeSideEffect) {
                field.onChangeSideEffect(
                  newValue as boolean,
                  formik.setFieldValue,
                );
              }
            }}
          />
        </div>
      );
    case "radio":
      return (
        <div className="investment-form__field" key={String(name)}>
          <RadioGroup
            name={String(name)}
            label={resolvedLabel}
            options={field.options || []}
            value={String(formik.values[name] || "")}
            onChange={(val) => formik.setFieldValue(String(name), val)}
            error={error}
            hasOtherInput={field.hasOtherInput}
            otherFieldName={field.otherFieldName}
            otherValue={
              field.otherFieldName
                ? String(formik.values[field.otherFieldName] || "")
                : ""
            }
            onOtherChange={
              field.otherFieldName
                ? (val) => formik.setFieldValue(field.otherFieldName!, val)
                : undefined
            }
          />
        </div>
      );
    case "computed":
      return (
        <div className="investment-form__field" key={name}>
          <ComputedField
            label={resolvedLabel}
            value={field.computeFn ? field.computeFn(formik.values) : ""}
            inline={field.inline}
            suffix={
              field.computeSuffix
                ? field.computeSuffix(formik.values, currencyOptions)
                : field.suffix
            }
          />
        </div>
      );
    case "date": {
      const minDate = field.computeMinDate
        ? field.computeMinDate(formik.values)
        : undefined;
      const maxDate = field.computeMaxDate
        ? field.computeMaxDate(formik.values)
        : undefined;
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            type="date"
            name={String(name)}
            label={resolvedLabel}
            optional={field.required === false}
            component={CalendarInput}
            errors={formik.errors}
            yearMonthPicker={true}
            minDate={minDate}
            maxDate={maxDate}
          />
        </div>
      );
    }
    case "file":
      return (
        <div className="investment-form__field" key={String(name)}>
          <Field
            label={resolvedLabel}
            name={String(name)}
            id={String(name)}
            as={DropFileInput}
            accept={field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"}
            optional
            multiple={field.multiple}
            maxSize={5}
            showPreview={field.showPreview ?? false}
            description={(() => {
              const formats = (
                field.accept ?? ".pdf,.doc,.docx,.jpg,.jpeg,.png"
              )
                .split(",")
                .map((s) => s.trim().replace(/^\./, "").toUpperCase())
                .join(", ");
              return field.multiple && field.multiple > 1
                ? `Maksymalnie ${field.multiple} plików, maks. 5\u00a0MB/plik (${formats})`
                : `Maks. 5\u00a0MB (${formats})`;
            })()}
          />
        </div>
      );
    case "switch":
      return (
        <div
          className="investment-form__field investment-form__field--switch"
          key={String(name)}
        >
          <LargeSwitch
            state={String(formik.values[name] || "")}
            options={field.options || []}
            onChange={(val: string) => formik.setFieldValue(String(name), val)}
          />
        </div>
      );
    default:
      return null;
  }
};

const InvestmentForm = ({
  slug,
  onSubmit,
  onCancel,
  initialData,
  isLoading = false,
  submitLabel = "Zapisz",
  isAdmin = false,
  checkIfCurrencyExists,
  checkIfCryptoExists,
}: InvestmentFormProps) => {
  const config = getFormConfig(slug);
  const validationSchema = getValidationSchema(slug, isAdmin);
  const initialValues = getInitialValues(slug, initialData);
  const { data: currencyOptions } = useGetCountriesCurrency();
  const { data: investmentCurrencyOptions } = useGetInvestmentCurrencies();
  const resolvedCurrencyOptions =
    slug === "currencies" ? investmentCurrencyOptions : currencyOptions;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const transformed = config.transformValues
          ? config.transformValues(values)
          : values;
        onSubmit(transformed as InvestmentFormValues);
      }}
      enableReinitialize
    >
      {(formik) => {
        const isCurrencyTransaction = !!(
          slug === "currencies" &&
          checkIfCurrencyExists &&
          formik.values.currencyUuid &&
          checkIfCurrencyExists(formik.values.currencyUuid as string)
        );

        const isExistingCryptoTransaction = !!(
          slug === "crypto" && formik.values.parentInvestmentUuid
        );

        return (
          <Form className="investment-form">
            <ScrollToFirstError />
            <div className="investment-form__fields">
              {config.fields.map((field) =>
                renderField(
                  field,
                  formik,
                  resolvedCurrencyOptions,
                  isCurrencyTransaction,
                  isAdmin,
                  isExistingCryptoTransaction,
                ),
              )}
            </div>

            <div className="investment-form__actions">
              <Button
                type="button"
                onClick={onCancel}
                label="Anuluj"
                className="button--stroke button--rounded"
                disabled={isLoading}
              />
              <Button
                type="submit"
                onClick={() => console.log()}
                label={submitLabel}
                className="button--rounded button--black"
                isLoading={isLoading}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default InvestmentForm;
